import React, { useState } from 'react';
import { Card, Statistic, Select, Tooltip } from 'antd';
import { DollarOutlined, InfoCircleOutlined, ReloadOutlined, SettingOutlined } from '@ant-design/icons';
import { useCustomerAccounts, useLoanEligibility } from '@/hooks/accounts/useCustomerAccount';
import { useAuthUser } from '@/modules/auth/context/AuthContext';
import { useTheme } from '@/components/common/ThemeProvider';

const { Option } = Select;

interface LoanEligibilityCardProps {
    className?: string;
}

export const LoanEligibilityCard: React.FC<LoanEligibilityCardProps> = ({ className }) => {
    const { dark } = useTheme();
    const authUser = useAuthUser();
    const { branch, custNo } = authUser;
    
    const [selectedProduct, setSelectedProduct] = useState<string | undefined>(undefined);
    const [showProductSelector, setShowProductSelector] = useState(false);
    
    const [productsQuery] = useCustomerAccounts(branch || '', custNo || '', 'L', true);
    const [loanEligibilityQuery, refetchEligibility] = useLoanEligibility(
        branch || '', 
        custNo || '', 
        selectedProduct
    );

    const eligibleAmount = Math.max(0, loanEligibilityQuery.data?.eligibleAmount || 0);
    const loanProducts = productsQuery.data || [];

    const handleProductChange = (value: string | undefined) => {
        setSelectedProduct(value);
    };

    const handleRefresh = () => {
        if (refetchEligibility) {
            refetchEligibility();
        }
    };

    return (
        <Card
            className={`shadow-lg hover:shadow-xl transition-shadow duration-300 border-0 overflow-hidden h-full ${
                dark ? 'bg-gradient-to-br from-green-900 to-green-800' : 'bg-gradient-to-br from-green-500 to-green-600'
            } ${className || ''}`}
            styles={{body: {padding: '24px', height: '100%', display: 'flex', flexDirection: 'column'}}}
        >
            <div className="flex items-start justify-between">
                <div className="flex-1">
                    <div className="flex items-center mb-2">
                        <DollarOutlined className="text-2xl text-white opacity-80 mr-2" />
                        <span className="text-white text-sm font-medium opacity-90">Loan Eligibility</span>
                        <Tooltip title="Select a loan product to check your eligibility. If no product is selected, the default product will be used.">
                            <InfoCircleOutlined className="text-white opacity-60 ml-2 cursor-help" />
                        </Tooltip>
                    </div>
                    <Statistic
                        value={loanEligibilityQuery.isLoading ? 0 : eligibleAmount}
                        precision={2}
                        valueStyle={{ color: '#fff', fontSize: '24px', fontWeight: 'bold' }}
                        prefix="TSH"
                        loading={loanEligibilityQuery.isLoading}
                    />
                </div>
                <div className="flex items-center gap-2">
                    {/* Settings icon - only visible on desktop */}
                    <Tooltip title={showProductSelector ? 'Hide product selector' : 'Change loan product'}>
                        <button
                            onClick={() => setShowProductSelector(!showProductSelector)}
                            className={`hidden lg:flex w-12 h-12 rounded-full items-center justify-center transition-all ${
                                dark ? 'bg-green-800 bg-opacity-50 hover:bg-opacity-70' : 'bg-white bg-opacity-20 hover:bg-opacity-30'
                            }`}
                        >
                            <SettingOutlined className={`text-xl text-white transition-transform ${showProductSelector ? 'rotate-90' : ''}`} />
                        </button>
                    </Tooltip>
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                        dark ? 'bg-green-800 bg-opacity-50' : 'bg-white bg-opacity-20'
                    }`}>
                        <DollarOutlined className="text-2xl text-white" />
                    </div>
                </div>
            </div>

            {/* Product selector - always visible on mobile, toggleable on desktop */}
            <div className={`mt-3 ${showProductSelector ? 'block' : 'hidden lg:hidden'}`}>
                <div className="flex items-center gap-2">
                    <Select
                        placeholder="Default Product"
                        onChange={handleProductChange}
                        value={selectedProduct}
                        allowClear
                        loading={productsQuery.isLoading}
                        className="flex-1"
                        size="small"
                        dropdownStyle={{ 
                            backgroundColor: dark ? '#1f2937' : '#ffffff',
                        }}
                    >
                        {loanProducts.map((product) => (
                            <Option key={product.glNo} value={product.glNo}>
                                {product.productName} ({product.glNo})
                                {product.maxAmount && ` - Max: ${product.maxAmount.toLocaleString()}`}
                            </Option>
                        ))}
                    </Select>
                    <Tooltip title="Refresh eligibility">
                        <button
                            onClick={handleRefresh}
                            disabled={loanEligibilityQuery.isLoading}
                            className={`px-3 py-1.5 text-white rounded transition-colors ${
                                loanEligibilityQuery.isLoading 
                                    ? 'bg-gray-500 cursor-not-allowed' 
                                    : 'bg-green-700 hover:bg-green-800'
                            }`}
                        >
                            <ReloadOutlined spin={loanEligibilityQuery.isLoading} />
                        </button>
                    </Tooltip>
                </div>
                
                <div className="text-white text-xs opacity-75 mt-2">
                    {selectedProduct ? 'Showing eligibility for selected product' : 'Using default loan product'}
                </div>
            </div>
        </Card>
    );
};

export default LoanEligibilityCard;
